import { RumInitConfiguration, datadogRum } from "@datadog/browser-rum";
import { App } from "@capacitor/app";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isProductionNodeEnvironment,
} from "@src/appV2/environment";
import { getCurrentBundleId } from "@src/mobile/utils/liveUpdate.adapter";
import { Platform } from "@src/mobile/utils/platform";
import { SessionType } from "src/modules/interface";

const datadogRumInitConfig: RumInitConfiguration = {
  applicationId: environmentConfig.REACT_APP_ADMIN_DATADOG_RUM_APPLICATION_ID,
  clientToken: environmentConfig.REACT_APP_ADMIN_DATADOG_RUM_CLIENT_TOKEN,
  service: "admin-web-app",
  defaultPrivacyLevel: "allow",
  version: __APP_VERSION__ ?? "1.0.0",
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  env: environmentConfig.REACT_APP_ENVIRONMENT_NAME,
  allowedTracingUrls: [
    environmentConfig.REACT_APP_API_MAIN_URL,
    /^https:\/\/.*\.prod\.clipboardhealth\.org/,
    /^https:\/\/.*\.clipboard\.health/,
  ],
  /**
   * On some iOS versions, cookies are blocked inside Capacitor WebViews.
   * Falling back to localStorage ensures RUM sessions are tracked correctly.
   * See: https://github.com/DataDog/browser-sdk/issues/480
   */
  allowFallbackToLocalStorage: true,
  enableExperimentalFeatures: ["feature_flags"],
};

async function getCapacitorAppVersion(): Promise<string | undefined> {
  if (!Platform.isMobile) {
    return undefined;
  }

  try {
    const info = await App.getInfo();
    return info.version;
  } catch {
    return undefined;
  }
}

export const initDatadogRum = async () => {
  if (
    isProductionNodeEnvironment() &&
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION
  ) {
    const appVersion = await getCapacitorAppVersion();
    const liveUpdateBundleId = await getCurrentBundleId();

    datadogRum.init({
      ...datadogRumInitConfig,
      beforeSend(event) {
        if (event.context) {
          event.context.screenWidth = window.screen.width;
          event.context.screenHeight = window.screen.height;
          event.context.platform = Platform.platform;
          event.context.isMobileApp = Platform.isMobile;
          if (appVersion) {
            event.context.appVersion = appVersion;
          }
          event.context.liveUpdateBundleId = liveUpdateBundleId;
        }
        return true;
      },
    });
  }
};

export const removeDatadogRUMUser = () => {
  datadogRum.clearUser();
};

export const identifyDatadogRumUser = (session: SessionType["session"]) => {
  const userProperties = getUserProperties(session);

  datadogRum.setUser({
    // As facility user names are not being searched for
    // we keep the facility's name instead of facility user's name for backward compatible
    name: session.profile?.name,
    email: session.user?.email,
    ...userProperties,
  });
};

export function setDatadogUserProperty(key: string, value: unknown) {
  datadogRum.setUserProperty(key, value);
}

function getSessionTypeProperties(session: SessionType["session"]) {
  if (session.type === "FACILITY") {
    return { facilityId: session.profile?.userId };
  }

  if (session.type === "AGENT") {
    return { agentId: session.profile?.userId };
  }

  return { adminId: session.profile?.userId };
}

function getUserProperties(session: SessionType["session"]) {
  if (session?.adminProfile) {
    return {
      type: "ADMIN",
      id: session.user?._id,
      sessionType: session.type,
      ...getSessionTypeProperties(session),
    };
  }

  return {
    type: "FACILITY",
    id: session.user?._id,
    facilityId: session.profile?.userId,
  };
}
